import { Team } from '../../_shared/models/team.model';
import { Platform } from '@ionic/angular';
import { ChangeDetectionStrategy, Component, Input, OnChanges, inject } from '@angular/core';
import { Match } from '../../_shared/models/match.model';
import { Participant } from '../../_shared/models/participant.model';
import { CourtSide } from '../../_shared/models/enums/court-side';
import { BoardHelpService } from '../../match-tracker/tracker-board/board-help.service';
import { Score } from '../../_shared/models/csTypes/score';
import { DisciplineEnum } from '../../_shared/models/csTypes/enums/discipline';
import { TimeItem } from '../../_shared/models/csTypes/time_item';

@Component({
  standalone: false,
  selector: 'app-player-box',
  templateUrl: './player-box.component.html',
  styleUrls: ['./player-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlayerBoxComponent implements OnChanges {
  private bhs = inject(BoardHelpService);
  platform = inject(Platform);

  @Input({ required: true }) match!: Match;
  @Input({ required: true}) score!: Score | null;
  @Input({ required: true}) sideNumber!: number;
  @Input({ required: true }) boxHeight!: number; // Not used at the moment
  @Input() team?: Team;
  @Input({ required: true }) screenPositionLeftRight!: string;
  @Input({ required: true }) screenPositionTopBottom!: string;
  @Input({ required: true }) sideColor!: string;

  player!: Participant | null;
  playerNumber!: number | null;
  courtSide: CourtSide | null = null;
  defaultStatus: TimeItem;

  ngOnChanges() {    
    this.player = this.getPlayer();
    this.courtSide = this.setCourtSides();
    this.setDefaultStatus();
  }

  private setCourtSides(): CourtSide | null {
    if (this.match.score?.server) {
      return (
        (this.screenPositionLeftRight === 'LEFT' && this.screenPositionTopBottom === 'BOTTOM') ||
        (this.screenPositionLeftRight === 'RIGHT' && this.screenPositionTopBottom === 'TOP')
      ) ? CourtSide.DEUCE : CourtSide.AD;
    }
    return null;
  }

  private getPlayer(): Participant | null {
    this.playerNumber = this.getPlayerNumber();
    return this.playerNumber
      ? this.match.sides[this.sideNumber - 1].players[this.playerNumber - 1].participant
      : null;
  }

  private getPlayerNumber(): number | null {  
    const scoreForamt = this.bhs.getScoreFormat(this.match.matchFormat, this.match.discipline);
    // If the server is not selected yet automatically place players  
    if (this.match.matchType === 'S') return 1;
    
    if (!this.match.score?.server || !this.match.score.server.playerNumber || this.match.discipline === DisciplineEnum.PICKLEBALL || this.match.discipline === DisciplineEnum.BADMINTON || scoreForamt.timedSetDuration) {
      if (this.sideNumber === 1) {
        if (this.screenPositionTopBottom === 'TOP') return 1;
        if (this.screenPositionTopBottom === 'BOTTOM') return 2;
      } else if (this.sideNumber === 2) {
        if (this.screenPositionTopBottom === 'TOP') return 1;
        if (this.screenPositionTopBottom === 'BOTTOM') return 2;
      }
    }

    const isSideServing = this.match.score?.server?.sideNumber === this.sideNumber;
    
    // If it is not a serving side place players based on the selection at the beginning of the set
    if (!isSideServing) {
      if (this.screenPositionLeftRight === 'LEFT' && this.screenPositionTopBottom === 'TOP') {
        return this.getReturnerPlayerNumber(this.sideNumber, CourtSide.AD);
      } else if (this.screenPositionLeftRight === 'LEFT' && this.screenPositionTopBottom === 'BOTTOM') {
        return this.getReturnerPlayerNumber(this.sideNumber, CourtSide.DEUCE)
      } else if (this.screenPositionLeftRight === 'RIGHT' && this.screenPositionTopBottom === 'TOP') {
        return this.getReturnerPlayerNumber(this.sideNumber, CourtSide.DEUCE);
      } else if (this.screenPositionLeftRight === 'RIGHT' && this.screenPositionTopBottom === 'BOTTOM') {
        return this.getReturnerPlayerNumber(this.sideNumber, CourtSide.AD)
      }
    } else {
      // If it is serving side flip server and returner for each point
      if (this.match.score?.server?.returningSide === CourtSide.DEUCE &&
        ((this.screenPositionLeftRight === 'LEFT' && this.screenPositionTopBottom === 'BOTTOM') ||
        (this.screenPositionLeftRight === 'RIGHT' && this.screenPositionTopBottom === 'TOP'))
      ) {
        return this.match.score!.server!.playerNumber!;
      } else if (this.match.score?.server?.returningSide === CourtSide.AD &&
        ((this.screenPositionLeftRight === 'LEFT' && this.screenPositionTopBottom === 'TOP') ||
        (this.screenPositionLeftRight === 'RIGHT' && this.screenPositionTopBottom === 'BOTTOM'))
      ) {
        return this.match.score.server.playerNumber!;
      } else {
        return this.match.score!.server!.playerNumber === 1 ? 2 : 1;
      }
    }
    return null;
  }

  private getReturnerPlayerNumber(sideNumber: number, courtSide: CourtSide): number | null {
    let playerNumber = null;
    if (this.match.matchStatus !== 'TBP') {
       playerNumber = this.bhs.getLastSet(this.match)?.returnerCourtSides?.find(rcs => {
        if (rcs.courtSide === courtSide && rcs.sideNumber === sideNumber) {
          return rcs;
        }
      })?.playerNumber || null;
    }        

    return playerNumber;
  }

  isServer(): boolean {
    const server = this.match.score?.server;
    if (!server) return false;

    if (server?.sideNumber === this.sideNumber) {
      if (!server.playerNumber) return false;
      if (server.sideNumber === this.sideNumber && server.playerNumber === this.playerNumber) return true;
    }

    return false;
  }

  setDefaultStatus(): void {
    const codeViolations = this.match?.timeItems?.filter(item =>
        item.itemValue.infringementType === 'CODE_VIOLATION' &&
        item.itemValue.penalty?.codeViolationType === 'DEFAULT'
      );
    this.defaultStatus = codeViolations?.[0] || { itemValue: {} } as TimeItem;
  }
}
